import { addDays, dayOfWeek, parts, toLocalDate } from '@homecare/domain';

/**
 * Every date and time the schedule screens show, formatted in one place.
 *
 * RECOVERY §2: three formatters once disagreed — the board in location time,
 * the list in browser time, the Move form in UTC — and opening the Move form
 * and saving without touching anything moved the visit. The rule since is
 * that nothing on these screens formats a date itself. A component either
 * shows the local fields the API already worked out (`localDate`,
 * `localStartTime`), or hands an instant to `instantText` **with a zone**.
 *
 * There is no zone-less overload and no browser-zone default. The browser's
 * zone is the agency's only by accident (R-1e), and a function that quietly
 * used it would be the fourth formatter.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const LONG_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "Tue 3 Nov" — a calendar day, which has no zone to get wrong. */
export function dayText(date: string): string {
  const day = toLocalDate(date);
  const { month, day: dom } = parts(day);
  return `${WEEKDAYS[dayOfWeek(day)]} ${dom} ${MONTHS[month - 1]}`;
}

/** "Tuesday" — for a screen reader, where "Tue" is read as a word. */
export function weekdayName(date: string): string {
  return LONG_WEEKDAYS[dayOfWeek(toLocalDate(date))] ?? '';
}

/** "1–7 Nov 2026", or "29 Nov – 5 Dec 2026" across a month. */
export function weekText(start: string, end: string): string {
  const a = parts(toLocalDate(start));
  const b = parts(toLocalDate(end));
  const tail = `${MONTHS[b.month - 1]} ${b.year}`;
  return a.month === b.month && a.year === b.year
    ? `${a.day}–${b.day} ${tail}`
    : `${a.day} ${MONTHS[a.month - 1]}${a.year === b.year ? '' : ` ${a.year}`} – ${b.day} ${tail}`;
}

/** The same day a week either side, for the board's arrows. */
export const shiftWeek = (date: string, weeks: number): string =>
  addDays(toLocalDate(date), weeks * 7);

/** "Tue 3 Nov, 10:00" for an instant, in the zone given — never the browser's. */
export function instantText(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

/** "8 h", "1 h 30 min", "45 min". */
export function minutesText(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** "4 min 12 s" for a refill — seconds matter when the target is two minutes. */
export function secondsText(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes === 0) return `${rest} s`;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}
